import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  BarChartOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExportOutlined,
  LockOutlined,
  ShareAltOutlined,
  UndoOutlined,
  UnlockOutlined,
} from '@ant-design/icons';

import {
  Button,
  Card,
  Dropdown,
  Flex,
  Input,
  InputRef,
  MenuProps,
  message,
  Modal,
  Popover,
  Skeleton,
  Tooltip,
} from 'antd';

import images from '../Thumbnail/Images';
import { PublishState } from '../../graphql/api';
import { Giide, IState, PasswordModalVariants } from '../../graphql/types';
import useUser from '../../hooks/useUser';
import styles from '../Components.module.css';
import {
  DEFAULT_THUMBNAIL_IMG,
  getSignedUrl,
  getUSDate,
  State,
} from '../utils';
import useGiideOperations from '../../hooks/useGiideOperations';
import {
  setAnalyticsID,
  setCopyGiideModal,
  setGiidePasswordModal,
  setGiideTabState,
} from '../../actions';
import AccountDisabledPopover from '../Account/AccountDisabledPopover';
import useExportVideo from '../../hooks/useSubscription/useExportVideo';
import useGiidePasswordOperation from '../PasswordProtection/useGiidePasswordOperation';

const disabledPopover = () => (
  <AccountDisabledPopover
    explanation={'Upgrade your plan to edit this giide.'}
  />
);

const GiideCard: React.FC<Giide> = ({
  id,
  title,
  publishState,
  cardDetails,
  passwordEnabled,
  owner,
  updatedAt,
  speaker,
  exportVideoState,
}) => {
  const dispatch = useDispatch();
  const [messageWithContext, contextHolder] = message.useMessage();

  const { push } = useHistory();
  const ref = React.createRef<InputRef>();
  const [overlay, setOverlay] = useState<boolean>(false);
  const [newTitle, setNewTitle] = useState<string>(title);
  const [thumbnailImg, setThumbnailImg] = useState<string | null>(null);
  const { user } = useUser();
  const [typedInRename, setTypedInRename] = useState<boolean>(false);

  const unlockedGiides = useSelector<IState, string[]>(
    state => state.unlockedGiides
  );

  const { isEnabled: userCanPasswordProtectGiide } =
    useGiidePasswordOperation();

  const isUnlocked = useMemo(() => {
    return unlockedGiides.includes(id);
  }, [unlockedGiides, id]);

  const userCanDuplicateGiide = useSelector<IState>(
    state => !!state.user?.plan.features?.giideDuplicate?.allowGiideDuplicate
  );

  const {
    updateGiideTitle,
    onDeleteGiide,
    onUnpublishGiide,
    giideTitleEditing,
    setGiideTitleEditing,
    deleteGiideModal,
  } = useGiideOperations(id);

  const { handleOpenExportModal, handleDownloadVideo } = useExportVideo();

  const accountDisabled = useSelector<IState, boolean>(
    state => state.accountDisabled
  );

  const giideUrl = React.useMemo<string>(
    () => `https://${process.env.REACT_APP_PLAYER_URL}/giide/${id}`,
    [id]
  );

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(giideUrl);
    messageWithContext.success('Copied to clipboard!');
  }, [giideUrl, messageWithContext]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;

    if (giideTitleEditing) {
      if (ref.current) {
        ref.current.focus();
      } else {
        timeout = setTimeout(() => ref.current?.focus(), 50);
      }
    }

    return () => clearTimeout(timeout);
  }, [giideTitleEditing, cardDetails, ref, thumbnailImg]);

  const onMenuItemClick: MenuProps['onClick'] = e => {
    e.domEvent.stopPropagation();
  };

  useEffect(() => {
    const ac = new AbortController();
    let isMounted = true;

    const fetchImage = async () => {
      try {
        let path = `public/${cardDetails?.thumbnailCustomPath}`;

        if (cardDetails?.thumbnailImageService === 'cloudinary') {
          path = `studio/${cardDetails.thumbnailCustomPath}`;
        }

        const data = await getSignedUrl(
          path,
          undefined,
          undefined,
          undefined,
          cardDetails?.thumbnailImageService
        );

        if (data) {
          const { url } = data;

          if (url) {
            const res = await fetch(url, { signal: ac.signal });

            if (res.ok) {
              const blob = await res.blob();

              if (isMounted) {
                setThumbnailImg(URL.createObjectURL(blob));
              }
            }
          }
        }
      } catch (e) {
        // console.log(e);
      }
    };

    if (
      !thumbnailImg &&
      cardDetails?.useCustomThumbnail &&
      cardDetails.thumbnailCustomPath
    ) {
      fetchImage();
    }

    return () => {
      isMounted = false;
      ac.abort();
    };
  }, [cardDetails, thumbnailImg, id]);

  const openGiide = React.useCallback(() => {
    if (passwordEnabled && !isUnlocked) {
      dispatch(setGiidePasswordModal(id, PasswordModalVariants.Check));
      return;
    }

    if (accountDisabled) {
      return;
    }

    if (!overlay) {
      // if (publishState === 'published') dispatch(setGiideTabState('audio'));
      if (publishState === 'published')
        dispatch(setGiideTabState('transcript'));
      else dispatch(setGiideTabState(undefined));
      push(`/giide/${id}`);
    }
  }, [
    id,
    push,
    overlay,
    dispatch,
    publishState,
    accountDisabled,
    passwordEnabled,
    isUnlocked,
  ]);

  const handleRenameInputChange = useCallback(
    e => {
      setNewTitle(e.target.value);
      if (!typedInRename) setTypedInRename(true);
    },
    [typedInRename]
  );

  const speakerName = speaker?.fullName ?? '';

  const [hovering, setHovering] = useState(false);

  const handleMouseEnter = () => {
    setHovering(true);
    setOverlay(false);
  };

  const handleMouseLeave = () => {
    setHovering(false);
  };

  const handleClickExportVideoStart = useCallback(() => {
    handleOpenExportModal(id, title);
  }, [handleOpenExportModal, id, title]);

  const handleClickDownloadVideo = useCallback(() => {
    handleDownloadVideo(id);
  }, [handleDownloadVideo, id]);

  const [loaded, setLoaded] = useState(false);

  const giideCardMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      {
        key: 'Analytics',
        disabled: accountDisabled,
        icon: <BarChartOutlined />,
        label: (
          <div onClick={() => dispatch(setAnalyticsID(id))}>Analytics</div>
        ),
      },
    ];

    if (publishState === PublishState.published) {
      items.push({
        key: 'url',
        icon: <ShareAltOutlined />,
        label: <div onClick={() => copyToClipboard()}>Get Share Url</div>,
      });

      if (exportVideoState !== 'complete') {
        items.push({
          key: 'export',
          disabled: accountDisabled,
          icon: <ExportOutlined />,
          label: (
            <div key={'export'} onClick={handleClickExportVideoStart}>
              Export To Video
            </div>
          ),
        });
      } else {
        items.push({
          key: 'export-download',
          disabled: accountDisabled,
          icon: <ExportOutlined />,
          label: <div onClick={handleClickDownloadVideo}>Download Video</div>,
        });
      }
    }

    items.push({
      key: 'edit',
      disabled: accountDisabled,
      icon: <EditOutlined />,
      label: <div onClick={() => setGiideTitleEditing(true)}>Rename</div>,
    });

    if (userCanDuplicateGiide) {
      items.push({
        key: 'duplicate',
        icon: <CopyOutlined />,
        disabled: accountDisabled,
        label: (
          <div onClick={() => dispatch(setCopyGiideModal(id))}>Duplicate</div>
        ),
      });
    }

    if (userCanPasswordProtectGiide && !passwordEnabled) {
      items.push({
        key: 'lock',
        disabled: accountDisabled,
        icon: <LockOutlined />,
        label: (
          <div
            onClick={() =>
              dispatch(setGiidePasswordModal(id, PasswordModalVariants.Create))
            }
          >
            Password Protect
          </div>
        ),
      });
    }

    if (userCanPasswordProtectGiide && passwordEnabled) {
      items.push({
        key: 'removePassword',
        disabled: accountDisabled,
        icon: <UnlockOutlined />,
        danger: true,
        label: (
          <div
            onClick={() =>
              dispatch(setGiidePasswordModal(id, PasswordModalVariants.Delete))
            }
          >
            Remove Password
          </div>
        ),
      });
    }

    if (
      publishState === PublishState.published &&
      (user?.userRole === 'admin' || user?.userId === owner)
    ) {
      items.push({
        key: 'unpublish',
        disabled: accountDisabled,
        icon: <UndoOutlined />,
        danger: true,
        label: <div onClick={() => onUnpublishGiide(false)}>Unpublish</div>,
      });
    }

    items.push({
      key: 'delete',
      disabled: accountDisabled,
      danger: true,
      icon: <DeleteOutlined />,
      label: <div onClick={onDeleteGiide}>Delete</div>,
    });

    return items;
  }, [
    accountDisabled,
    copyToClipboard,
    passwordEnabled,
    dispatch,
    exportVideoState,
    handleClickDownloadVideo,
    handleClickExportVideoStart,
    id,
    onDeleteGiide,
    onUnpublishGiide,
    owner,
    publishState,
    setGiideTitleEditing,
    user?.userId,
    user?.userRole,
    userCanPasswordProtectGiide,
    userCanDuplicateGiide,
  ]);

  const [imageTimeoutExceeded, setImageTimeoutExceeded] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setImageTimeoutExceeded(true), 5000);

    return () => clearTimeout(timeout);
  }, []);

  const coverImage = useMemo(() => {
    return (
      <div style={{ position: 'relative', width: '100%', height: '167pt' }}>
        {cardDetails?.useCustomThumbnail && thumbnailImg ? (
          <img
            className="giideCardImage"
            src={thumbnailImg}
            alt="custom"
            onLoad={() => setLoaded(true)}
            onError={() => setImageTimeoutExceeded(true)}
            style={{
              opacity: loaded ? 1 : 0,
              transition: 'opacity 0.2s',
              position: 'relative',
            }}
          />
        ) : !cardDetails?.useCustomThumbnail ? (
          <img
            className={`giideCardImage ${styles.giideCardImageNoCustom}`}
            src={
              cardDetails?.thumbnailImg
                ? images.find(
                    element => element.id === cardDetails.thumbnailImg
                  )?.src
                : images.find(element => element.id === DEFAULT_THUMBNAIL_IMG)
                    ?.src
            }
            alt="custom"
            onLoad={() => setLoaded(true)}
            onError={() => setImageTimeoutExceeded(true)}
            style={{
              opacity: loaded ? 1 : 0,
            }}
          />
        ) : (
          <Skeleton.Image
            active={!imageTimeoutExceeded}
            className={styles.giideCardSkeleton}
          />
        )}

        {!loaded && !cardDetails?.useCustomThumbnail && (
          <Skeleton.Image
            active={!imageTimeoutExceeded}
            className={styles.giideCardSkeleton}
            style={{
              opacity: !loaded && !imageTimeoutExceeded ? 1 : 0,
              transition: 'opacity 0.2s',
            }}
          />
        )}
      </div>
    );
  }, [loaded, cardDetails, thumbnailImg, imageTimeoutExceeded]);

  return (
    <>
      {contextHolder}
      <Modal
        centered
        title="Rename Giide"
        open={giideTitleEditing}
        styles={{ body: { margin: '16px 0' } }}
        onCancel={() => setGiideTitleEditing(false)}
        onOk={() => updateGiideTitle(newTitle)}
      >
        <Input
          ref={ref}
          size={'middle'}
          onChange={handleRenameInputChange}
          onClick={event => event.stopPropagation}
          onPressEnter={() => updateGiideTitle(newTitle)}
          value={newTitle}
          maxLength={40}
          placeholder="Project01"
        />
      </Modal>
      {deleteGiideModal}
      <Popover
        placement={'bottom'}
        open={accountDisabled && hovering}
        content={disabledPopover}
      >
        <Card
          hoverable={!accountDisabled}
          bordered={true}
          className={'giide-card'}
          style={{ width: '167pt' }}
          cover={coverImage}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={openGiide}
          bodyStyle={{ height: '124px', padding: '1rem' }}
        >
          <Card.Meta
            title={
              <Flex justify={'space-between'} align={'flex-start'}>
                <div className={styles.giideTitle}>{title}</div>
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  disabled={
                    accountDisabled || (!!passwordEnabled && !isUnlocked)
                  }
                  menu={{
                    items: giideCardMenuItems,
                    onClick: onMenuItemClick,
                  }}
                >
                  <Button
                    icon={<EllipsisOutlined style={{ fontSize: '24px' }} />}
                    shape={'circle'}
                    type={'text'}
                    onClick={e => e.stopPropagation()}
                  ></Button>
                </Dropdown>
              </Flex>
            }
            description={
              <div>
                <div className={styles.giideAuthor}>{speakerName}</div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div className={styles.giideState}>
                    <div className={styles.giidePublishState}>
                      {State[publishState || 'unpublished']}
                    </div>
                    <div>·</div>
                    <div className={styles.dateText}>
                      Last Edited {updatedAt ? getUSDate(updatedAt) : ''}
                    </div>
                  </div>

                  {passwordEnabled &&
                    (isUnlocked ? (
                      <Tooltip
                        title={'This giide is password protected but unlocked.'}
                      >
                        <UnlockOutlined />
                      </Tooltip>
                    ) : (
                      <Tooltip title={'This giide is password protected.'}>
                        <LockOutlined />
                      </Tooltip>
                    ))}
                </div>
              </div>
            }
          ></Card.Meta>
        </Card>
      </Popover>
    </>
  );
};

export default GiideCard;
